import React, { useEffect, useState } from "react";
import {
  Box, Typography, Avatar, Collapse, CircularProgress, Chip,
} from "@mui/material";
import { UserTreeApi } from "../../api/requests/network/network";
import GetValidatedTokenData from "../../utils/helper";
import PersonIcon from "@mui/icons-material/Person";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import GroupIcon from "@mui/icons-material/Group";

const findMyNode = (nodes, myCode) => {
  if (!nodes || !Array.isArray(nodes)) return null;
  for (const node of nodes) {
    const code = node.attributes?.shortcode || node.shortcode || "";
    if (code === myCode) return node;
    if (node.children?.length > 0) {
      const found = findMyNode(node.children, myCode);
      if (found) return found;
    }
  }
  return null;
};

const countAll = (arr) => {
  if (!arr || !Array.isArray(arr)) return 0;
  return arr.reduce((sum, n) => sum + 1 + countAll(n.children), 0);
};

const COLORS = [
  "#2563EB", "#059669", "#7C3AED", "#D97706", "#DC2626",
  "#0891B2", "#4F46E5", "#16A34A", "#EA580C", "#9333EA",
];

function TreeNode({ node, level = 0, isLast = false }) {
  const [open, setOpen] = useState(level < 1);
  const name = node.name || "Member";
  const shortcode = node.attributes?.shortcode || node.shortcode || "---";
  const children = node.children || [];
  const totalTeam = countAll(children);
  const avatarColor = COLORS[level % COLORS.length];

  return (
    <Box>
      {/* Connector line */}
      <Box sx={{ display: "flex", alignItems: "stretch" }}>
        {/* Indent guides */}
        {level > 0 && (
          <Box sx={{ width: level * 32, flexShrink: 0, position: "relative" }}>
            <Box sx={{
              position: "absolute", left: (level - 1) * 32 + 16, top: 0, bottom: isLast ? "50%" : 0,
              borderLeft: "1px solid #E2E8F0",
            }} />
            <Box sx={{
              position: "absolute", left: (level - 1) * 32 + 16, top: "50%",
              width: 16, borderTop: "1px solid #E2E8F0",
            }} />
          </Box>
        )}

        {/* Node card */}
        <Box
          onClick={() => children.length > 0 && setOpen(!open)}
          sx={{
            flex: 1, display: "flex", alignItems: "center", gap: 1.5,
            py: 1, px: 1.5, my: 0.3,
            borderRadius: "10px", backgroundColor: "#FFFFFF",
            border: "1px solid #F1F5F9",
            cursor: children.length > 0 ? "pointer" : "default",
            transition: "all 0.15s ease",
            "&:hover": {
              backgroundColor: "#F8FAFC",
              borderColor: "#E2E8F0",
            },
          }}
        >
          {/* Expand/collapse or person icon */}
          {children.length > 0 ? (
            <Box sx={{
              width: 24, height: 24, display: "flex", alignItems: "center", justifyContent: "center",
              borderRadius: "6px", backgroundColor: "#F1F5F9", flexShrink: 0,
              transition: "transform 0.2s",
              transform: open ? "rotate(0deg)" : "rotate(-90deg)",
            }}>
              <ExpandMoreIcon sx={{ fontSize: 16, color: "#64748B" }} />
            </Box>
          ) : (
            <Box sx={{ width: 24, height: 24, flexShrink: 0 }} />
          )}

          {/* Avatar */}
          <Avatar sx={{
            width: 34, height: 34, fontSize: 14, fontWeight: 400,
            backgroundColor: `${avatarColor}14`, color: avatarColor,
            flexShrink: 0,
          }}>
            {name.charAt(0).toUpperCase()}
          </Avatar>

          {/* Info */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{
              fontSize: "13px", fontWeight: 500, color: "#1E293B",
              lineHeight: 1.3, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
            }}>
              {name}
            </Typography>
            <Typography sx={{ fontSize: "11px", fontWeight: 400, color: "#94A3B8", lineHeight: 1.3 }}>
              {shortcode}
            </Typography>
          </Box>

          {/* Team badge */}
          {totalTeam > 0 && (
            <Chip
              icon={<GroupIcon sx={{ fontSize: 12 }} />}
              label={totalTeam}
              size="small"
              sx={{
                height: 22, fontSize: "11px", fontWeight: 400,
                backgroundColor: "#EFF6FF", color: "#2563EB",
                "& .MuiChip-icon": { color: "#2563EB", ml: 0.5 },
                "& .MuiChip-label": { px: 0.5 },
              }}
            />
          )}

          {/* Level badge */}
          {level > 0 && (
            <Typography sx={{
              fontSize: "10px", fontWeight: 400, color: "#94A3B8",
              backgroundColor: "#F8FAFC", px: 0.8, py: 0.2, borderRadius: "4px",
              whiteSpace: "nowrap",
            }}>
              L{level}
            </Typography>
          )}
        </Box>
      </Box>

      {/* Children */}
      {children.length > 0 && (
        <Collapse in={open} timeout={200}>
          <Box>
            {children.map((child, i) => (
              <TreeNode
                key={child.attributes?.shortcode || child.shortcode || i}
                node={child}
                level={level + 1}
                isLast={i === children.length - 1}
              />
            ))}
          </Box>
        </Collapse>
      )}
    </Box>
  );
}

function MembersTree() {
  const [treeData, setTreeData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchNetwork = async () => {
      try {
        const res = await UserTreeApi.getUserNetwork();
        const allNodes = res?.data || (Array.isArray(res) ? res : []);

        const tokenData = GetValidatedTokenData();
        const myCode = tokenData?.userPayload?.shortcode || "";

        let mySubtree = [];
        if (myCode) {
          const myNode = findMyNode(allNodes, myCode);
          if (myNode) mySubtree = [myNode];
        }
        if (mySubtree.length === 0) {
          mySubtree = allNodes.length > 0 ? allNodes : [];
        }

        setTreeData(mySubtree);
      } catch (error) {}
      setLoading(false);
    };
    fetchNetwork();
  }, []);

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", py: 8 }}>
        <CircularProgress size={28} sx={{ color: "#2563EB" }} />
      </Box>
    );
  }

  if (treeData.length === 0) {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", py: 8 }}>
        <Avatar sx={{ width: 48, height: 48, mb: 1.5, backgroundColor: "#EFF6FF", color: "#2563EB" }}>
          <PersonIcon sx={{ fontSize: 24 }} />
        </Avatar>
        <Typography sx={{ fontSize: "14px", fontWeight: 400, color: "#64748B" }}>
          No network members yet
        </Typography>
        <Typography sx={{ fontSize: "12px", fontWeight: 400, color: "#94A3B8", mt: 0.5 }}>
          Invite members to grow your team
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ maxHeight: 520, overflowY: "auto", pr: 0.5 }}>
      {treeData.map((rootNode, i) => (
        <TreeNode key={rootNode.attributes?.shortcode || rootNode.shortcode || i} node={rootNode} level={0} />
      ))}
    </Box>
  );
}

export default MembersTree;
